import React from 'react';
import { Card } from 'components/Card';
import { InfoTooltip } from 'components/InfoTooltip';
import { Box, Stack, Typography, TextField } from '@mui/material';
import { useCampaignForm } from 'hooks';
import { CampaignFields, CampaignFormInputs, Website } from 'types';
import { Controller, Path } from 'react-hook-form';
import { TargetingSuggestions } from './TargetingSuggestions';

interface TargetingTextDescriptionProps {
  website?: Website;
  websiteIndex?: number;
  suggestions?: string[];
  isSuggestionLoading?: boolean;
  handleGenerateSuggestion: () => void;
}

export const TargetingTextDescription = ({
  website,
  websiteIndex,
  suggestions,
  isSuggestionLoading,
  handleGenerateSuggestion,
}: TargetingTextDescriptionProps) => {
  const { control, formState, onFieldChange } = useCampaignForm();
  const error = formState?.errors?.[CampaignFields.targetingGuidance]?.message;

  const rules = {
    maxLength: { value: 2000, message: 'Maximal 2000 Zeichen erlaubt.' },
  };

  const fieldName: Path<CampaignFormInputs> =
    websiteIndex !== undefined ? `websites.${websiteIndex}.targetingGuidance` : CampaignFields.targetingGuidance;

  return (
    <Card>
      <Stack gap={6}>
        <Stack gap={4}>
          {website?.url ? (
            <Typography sx={{ fontWeight: 700, fontSize: '20px', mb: 1 }}>{website.url}</Typography>
          ) : null}
          {/* Header with info */}
          <Box display="flex" alignItems={{ xs: 'flex-start', md: 'center' }} gap={1}>
            <Typography variant="subtitle1" sx={{ mr: 1 }}>
              Beschreibe deine Zielgruppe in ein paar Sätzen oder Stichworten
            </Typography>
            <Box>
              <InfoTooltip
                placement="bottom"
                text={
                  <Typography variant="body2">
                    Beschreibe, wen du mit deiner Kampagne erreichen möchtest – z. B. Alter, Interessen, Region oder
                    Sprache. Du kannst ganze Sätze oder Stichworte verwenden.
                    <br />
                    <br />
                    Oder übernimm einen der unten generierten Vorschläge. Diese wurden basierend auf deinen Webseiten
                    und Creatives für dich erstellt.
                  </Typography>
                }
              />
            </Box>
          </Box>

          {/* Textarea */}
          <Controller
            name={fieldName}
            control={control}
            rules={rules}
            render={({ field: { value, onChange } }) => {
              return (
                <TextField
                  // if user got targeting config object set and choose text description just ignore object
                  value={typeof value === 'object' ? '' : value}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChange(e);
                    onFieldChange(fieldName);
                  }}
                  label="Zielgruppe deiner Kampagne"
                  placeholder="Beschreibe deine Zielgruppe oder füge einen der Vorschläge hier ein."
                  multiline
                  minRows={4}
                  maxRows={8}
                  fullWidth
                  variant="outlined"
                  size="small"
                  error={!!error}
                  helperText={error}
                  sx={{
                    '& .MuiInputBase-root.MuiOutlinedInput-root': {
                      padding: '8px',
                      background: '#FFFFFF',
                    },
                    '& .MuiInputBase-input.MuiInputBase-inputMultiline': {
                      padding: '7px',
                      resize: 'auto',
                    },
                  }}
                />
              );
            }}
          />
        </Stack>
        <TargetingSuggestions
          suggestions={suggestions}
          isLoading={isSuggestionLoading}
          onGenerate={handleGenerateSuggestion}
        />
      </Stack>
    </Card>
  );
};
